import ChevronLeft from "@mui/icons-material/ChevronLeft";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { CancelButton } from "core/components/CancelButton";
import { PageContainer } from "core/components/PageContainer";
import { PageHeader } from "core/components/PageHeader";
import { ReactHookEmailInput } from "core/components/ReactHookFormComponents/ReactHookEmailInput";
import { ReactHookFormSelect } from "core/components/ReactHookFormComponents/ReactHookFormSelect";
import { ReactHookTextField } from "core/components/ReactHookFormComponents/ReactHookTextField";
import { ResetButton } from "core/components/ResetButton";
import { SubmitButton } from "core/components/SubmitButton";
import type { CentrixExtractRequestBody } from "core/entities/CentrixTypes";
import { useBrand } from "core/hooks/useBrand";
import { useCentrixFetch } from "core/hooks/useCentrixFetch";
import { usePageLogging } from "core/hooks/usePageLogging";
import { usePublicShopOptions } from "core/hooks/usePublicShopOptions";
import { useRegion } from "core/hooks/useRegion";
import { useToaster } from "core/hooks/useToaster";
import { useValidationErrorToaster } from "core/hooks/useValidationErrorToaster";
import { FavoriteButton } from "features/MyLinks/components";
import { useRouter } from "next/router";
import { type FieldErrorsImpl, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

type TrainingRequestCreateDto =
  CentrixExtractRequestBody<"/api/app/training-requests">;

const defaultValues: TrainingRequestCreateDto = {
  action: "",
  firstName: "",
  lastName: "",
  email: "",
  language: "",
  job: "",
  manager: "",
  shopId: "",
};

export function TrainingRequestPage() {
  usePageLogging({
    itemType: "Page",
    itemId: "training-request",
    componentName: "TrainingRequestPage",
  });
  const { locales } = useRouter();
  const { back } = useRouter();
  const centrixFetch = useCentrixFetch();
  const { successToast, errorToast } = useToaster();
  const validationErrorToaster = useValidationErrorToaster();
  const region = useRegion();
  const { brandTwoLetterCode } = useBrand();
  const { options: shopOptions, isLoading: isShopOptionsLoading } =
    usePublicShopOptions({
      filter: {
        ShopStatus: "Active",
        BrandTwoLetterCodes: [brandTwoLetterCode],
        CountryIso2AlphaCodes: [region],
      },
      fetchAll: true,
    });
  const formMethods = useForm({ defaultValues });
  const { isSubmitting } = formMethods.formState;
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:TrainingRequest",
  });
  const { t: tBase } = useTranslation();
  const successMessage = t("submitSuccess");
  const errorMessage = t("submitError");

  async function onValidSubmit(form: TrainingRequestCreateDto) {
    const trainingRequestResponse = await centrixFetch({
      method: "post",
      path: "/api/app/training-requests",
      body: form,
    });
    if (!trainingRequestResponse.ok) {
      errorToast(errorMessage);
      return;
    }
    successToast(successMessage);
    formMethods.reset(defaultValues);
  }

  function onInvalidSubmit(errors: FieldErrorsImpl<TrainingRequestCreateDto>) {
    validationErrorToaster(Object.keys(errors).map((name) => t(name)));
  }

  const actionOptions: { label: string; value: string }[] = [
    { label: t("action.add"), value: t("action.add") },
    { label: t("action.remove"), value: t("action.remove") },
    { label: t("action.update"), value: t("action.update") },
  ];

  const localeOptions =
    locales
      ?.filter((locale) => locale !== "default")
      .map((locale) => {
        const value = tBase(`Next:Core:LanguageCodes.${locale}`);
        return { label: value, value };
      }) ?? [];

  return (
    <PageContainer maxWidth="md">
      <PageHeader
        title={t("pageTitle")}
        subTitle={t("subTitle")}
        TitleSlot={
          <FavoriteButton
            itemId="training-request"
            itemType="Page"
            title={t("pageTitle")}
          />
        }
        RightSlot={
          <Button startIcon={<ChevronLeft />} variant="outlined" onClick={back}>
            {t("goBack")}
          </Button>
        }
      />
      <Stack
        gap={2}
        component="form"
        onSubmit={formMethods.handleSubmit(onValidSubmit, onInvalidSubmit)}
      >
        <ReactHookFormSelect
          required
          control={formMethods.control}
          name="action"
          label={t("action")}
          options={actionOptions}
        />
        <ReactHookTextField
          name="firstName"
          control={formMethods.control}
          autoComplete="given-name"
          label={t("firstName")}
          required
        />
        <ReactHookTextField
          name="lastName"
          control={formMethods.control}
          autoComplete="family-name"
          label={t("lastName")}
          required
        />
        <ReactHookEmailInput
          name="email"
          required
          control={formMethods.control}
          textFieldProps={{
            InputProps: {
              startAdornment: (
                <InputAdornment position="start">
                  <Tooltip title={t("emailTooltipTitle")}>
                    <InfoOutlined fontSize="small" />
                  </Tooltip>
                </InputAdornment>
              ),
            },
          }}
          label={t("email")}
        />
        <ReactHookFormSelect
          required
          control={formMethods.control}
          loading={isShopOptionsLoading}
          name="shopId"
          label={t("shopId")}
          options={shopOptions}
        />
        <ReactHookFormSelect
          required
          control={formMethods.control}
          name="language"
          label={t("language")}
          options={localeOptions}
        />
        <ReactHookTextField
          control={formMethods.control}
          name="job"
          label={t("job")}
          required
        />
        <ReactHookTextField
          control={formMethods.control}
          required
          name="manager"
          label={t("manager")}
        />

        <Stack direction="row" gap={2} justifyContent="flex-end">
          <CancelButton onClick={back} />
          <ResetButton
            onClick={() => {
              formMethods.reset(defaultValues);
            }}
          />
          <SubmitButton loading={isSubmitting} />
        </Stack>
      </Stack>
    </PageContainer>
  );
}
